import React, { useState, useEffect } from 'react';
import { portalPromotionsAPI } from '../../services/portalAPI';

interface Promotion {
  id: number;
  title: string;
  message: string;
  image_url: string | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_current: boolean;
  status: 'active' | 'scheduled' | 'expired' | 'inactive';
  created_at: string;
  created_by_name: string | null;
}

interface PromotionFormData {
  title: string;
  message: string;
  start_date: string;
  end_date: string;
  image: File | null;
}

const PortalPromotions: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<PromotionFormData>({
    title: '',
    message: '',
    start_date: '',
    end_date: '',
    image: null,
  });
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      const response = await portalPromotionsAPI.getAll();
      setPromotions(response.data.promotions || []);
      setError(null);
    } catch (err: any) {
      setError('Failed to load promotions');
      console.error('Error loading promotions:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      start_date: '',
      end_date: '',
      image: null,
    });
    setEditingId(null);
    setShowForm(false);
    setFormError(null);
  };

  const handleEdit = (promo: Promotion) => {
    setFormData({
      title: promo.title,
      message: promo.message,
      start_date: promo.start_date,
      end_date: promo.end_date,
      image: null,
    });
    setEditingId(promo.id);
    setShowForm(true);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);

    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('message', formData.message);
      data.append('start_date', formData.start_date);
      data.append('end_date', formData.end_date);
      if (formData.image) {
        data.append('image', formData.image);
      }

      if (editingId) {
        await portalPromotionsAPI.update(editingId, data);
      } else {
        await portalPromotionsAPI.create(data);
      }

      await loadPromotions();
      resetForm();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to save promotion');
      console.error('Error saving promotion:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) {
      return;
    }

    try {
      await portalPromotionsAPI.delete(id);
      await loadPromotions();
    } catch (err: any) {
      alert('Failed to delete promotion');
      console.error('Error deleting promotion:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, React.CSSProperties> = {
      active: { background: '#DEF7EC', color: '#03543F', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 },
      scheduled: { background: '#E1EFFE', color: '#1E40AF', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 },
      expired: { background: '#F3F4F6', color: '#6B7280', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 },
      inactive: { background: '#FEE2E2', color: '#991B1B', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 },
    };
    return <span style={styles[status] || styles.inactive}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading promotions...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Store Promotions</h2>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6B7280' }}>
            Create promotions for your Bridge Club members
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              background: '#E73C3E',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            + New Promotion
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>
            {editingId ? 'Edit Promotion' : 'Create New Promotion'}
          </h3>

          {formError && (
            <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., 20% Off All Poultry Feed!"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                Message *
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Describe your promotion, include any terms or conditions..."
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                Promotional Image (optional)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({ ...formData, image: e.target.files?.[0] || null })}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>
                Recommended: 800x400 pixels, JPG or PNG
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  background: saving ? '#9CA3AF' : '#E73C3E',
                  color: 'white',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: 500,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? 'Saving...' : editingId ? 'Update Promotion' : 'Create Promotion'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Promotions List */}
      {promotions.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '12px', padding: '40px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: '48px', margin: '0 0 12px' }}>📢</p>
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: '#111827' }}>No Promotions Yet</h3>
          <p style={{ margin: '0 0 16px', color: '#6B7280', fontSize: '14px' }}>
            Create your first promotion to engage your Bridge Club members!
          </p>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              style={{
                background: '#E73C3E',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Create Your First Promotion
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {promotions.map((promo) => (
            <div
              key={promo.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                gap: '16px',
                opacity: promo.status === 'expired' || promo.status === 'inactive' ? 0.7 : 1,
              }}
            >
              {promo.image_url && (
                <img
                  src={promo.image_url}
                  alt={promo.title}
                  style={{
                    width: '120px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    flexShrink: 0,
                  }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>{promo.title}</h4>
                  {getStatusBadge(promo.status)}
                </div>
                <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#4B5563', lineHeight: 1.4 }}>
                  {promo.message.length > 150 ? promo.message.substring(0, 150) + '...' : promo.message}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ margin: 0, fontSize: '12px', color: '#9CA3AF' }}>
                    {formatDate(promo.start_date)} - {formatDate(promo.end_date)}
                  </p>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEdit(promo)}
                      style={{
                        background: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(promo.id)}
                      style={{
                        background: '#FEE2E2',
                        color: '#991B1B',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalPromotions;
